import React, { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react";
import { toast } from "react-toastify";
import Question from "./Question";
import "./Section.scss";
import ButtonWithIcon from "../ButtonwithIcon/ButtonWithIcon";
import IconButton from "../IconButton/IconButton";
import { QUESTION_TYPE_LIST } from "./questionTypes";
import {
  getSectionMaxScore,
  generateScoreBands,
  applyBandSync,
  createSingleChoiceOptions,
  createMultiChoiceOptions,
  createRatingOptions,
  DEFAULT_RATING_COUNT,
} from "./scoringUtils";

const blankInterp = () => ({
  id: null,
  title: "",
  minScore: 0,
  maxScore: 0,
  description: "",
  isNew: true,
  isChanged: false,
  isDeleted: false,
});
const blankRec = () => ({
  id: null,
  title: "",
  minScore: 0,
  maxScore: 0,
  recommendationText: "",
  isNew: true,
  isChanged: false,
  isDeleted: false,
});

// ── one score band = one range + its interpretation + its recommendation ──
// Shown together so it's obvious this scoring belongs to this section.
const BandCard = ({
  range,
  onRangeChange,
  interp,
  onInterpChange,
  rec,
  onRecChange,
  onDelete,
  showDelete,
}) => (
  <div className="sec-band">
    <div className="sec-band__hd">
      <span className="sec-band__range">
        <input
          type="number"
          title="Minimum score (inclusive)"
          placeholder="0"
          value={range.minScore ?? ""}
          onChange={(e) => onRangeChange("minScore", e.target.value)}
        />
        <span>–</span>
        <input
          type="number"
          title="Maximum score (inclusive)"
          placeholder="10"
          value={range.maxScore ?? ""}
          onChange={(e) => onRangeChange("maxScore", e.target.value)}
        />
      </span>
      {showDelete && (
        <IconButton
          className="sec-band__del"
          icon="material-symbols-light:delete-outline"
          variant="delete"
          onClick={onDelete}
          title="Remove this score band"
        />
      )}
    </div>
    <div className="sec-band__cols">
      <div className="sec-band__col">
        <span className="sec-band__col-label">
          <Icon icon="mdi:text-box-outline" /> Interpretation
        </span>
        <input
          placeholder="e.g. High performer"
          value={interp.title || ""}
          onChange={(e) => onInterpChange("title", e.target.value)}
        />
        <textarea
          rows={2}
          placeholder="What this score range means…"
          value={interp.description || ""}
          onChange={(e) => onInterpChange("description", e.target.value)}
        />
      </div>
      <div className="sec-band__col">
        <span className="sec-band__col-label">
          <Icon icon="mdi:lightbulb-outline" /> Recommendation
        </span>
        <input
          placeholder="e.g. Keep it up"
          value={rec.title || ""}
          onChange={(e) => onRecChange("title", e.target.value)}
        />
        <textarea
          rows={2}
          placeholder="What the respondent should do…"
          value={rec.recommendationText || ""}
          onChange={(e) => onRecChange("recommendationText", e.target.value)}
        />
      </div>
    </div>
  </div>
);

// ── Section ───────────────────────────────────────────────────────────────────
// `restricted` mirrors FormBuilder's `restrictToExisting`: true once the
// parent test already has responses. In that state this section can still
// be edited in place (titles, text, option wording, score ranges) but
// nothing new can be created and nothing existing can be removed — that
// would change the shape of results people have already gotten.
const Section = ({
  section,
  index,
  onUpdate,
  onDelete,
  searchQuery = "",
  focusedQuestion = null,
  restricted = false,
}) => {
  // First section is expanded by default; a freshly-added section always
  // opens straight into edit mode too.
  const [open, setOpen] = useState(index === 0 || !!section.isNew);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const dragFromRef = useRef(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [bandCount, setBandCount] = useState(3);
  const [questionFocus, setQuestionFocus] = useState(null);
  const sectionDomId = section.sectionId || `new-${index}`;

  useEffect(() => {
    if (focusedQuestion?.sectionKey !== sectionDomId) return;
    setOpen(true);
    setQuestionFocus({
      ai: focusedQuestion.questionIndex,
      token: focusedQuestion.token,
    });
    requestAnimationFrame(() => {
      document
        .getElementById(
          `question-${sectionDomId}-${focusedQuestion.questionIndex}`,
        )
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [focusedQuestion, sectionDomId]);

  const updateField = useCallback(
    (field, value) => {
      onUpdate({ ...section, [field]: value, isChanged: !section.isNew });
    },
    [section, onUpdate],
  );

  const ensureDefaults = useCallback(() => {
    // Once the test has responses, don't fabricate a starter question or
    // starter bands even if this section happens to have none loaded — the
    // whole point of `restricted` is that nothing new gets created.
    if (restricted) return;
    let changed = false;
    const updates = { ...section };
    if (!section.questions) {
      updates.questions = [];
      changed = true;
    }
    // A section with zero questions isn't useful yet — start it off with
    // one blank question already in place instead of making the person
    // click "Add your first question" first.
    if (!(updates.questions || []).filter((qq) => !qq.isDeleted).length) {
      updates.questions = [
        ...(updates.questions || []),
        {
          questionId: null,
          questionText: "",
          answerType: "SINGLE_CHOICE",
          questionOrder: 1,
          isNew: true,
          isChanged: false,
          isDeleted: false,
          answerOptions: createSingleChoiceOptions(),
        },
      ];
      changed = true;
    }
    if (!section.sectionInterpretation?.length) {
      updates.sectionInterpretation = [blankInterp()];
      changed = true;
    }
    if (!section.sectionRecommendations?.length) {
      updates.sectionRecommendations = [blankRec()];
      changed = true;
    }
    // Interpretations and recommendations are shown paired as one "band"
    // per index, so keep the two (visible) lists the same length.
    const visI = (updates.sectionInterpretation || []).filter(
      (x) => !x.isDeleted,
    ).length;
    const visR = (updates.sectionRecommendations || []).filter(
      (x) => !x.isDeleted,
    ).length;
    if (visI > visR) {
      updates.sectionRecommendations = [
        ...updates.sectionRecommendations,
        ...Array.from({ length: visI - visR }, blankRec),
      ];
      changed = true;
    } else if (visR > visI) {
      updates.sectionInterpretation = [
        ...updates.sectionInterpretation,
        ...Array.from({ length: visR - visI }, blankInterp),
      ];
      changed = true;
    }
    if (changed) onUpdate({ ...updates, isChanged: !updates.isNew });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    ensureDefaults();
  }, [ensureDefaults]);

  const addQuestion = (answerType = "SINGLE_CHOICE") => {
    if (restricted) {
      toast.error("This test has responses — new questions can't be added.");
      setAddMenuOpen(false);
      return;
    }
    const base = {
      questionId: null,
      questionText: "",
      answerType,
      questionOrder:
        (section.questions?.filter((q) => !q.isDeleted)?.length || 0) + 1,
      isNew: true,
      isChanged: false,
      isDeleted: false,
    };
    const withOptions = {
      answerOptions:
        answerType === "MULTI_CHOICE"
          ? createMultiChoiceOptions()
          : answerType === "RATING"
            ? createRatingOptions(DEFAULT_RATING_COUNT)
            : createSingleChoiceOptions(),
    };
    updateField("questions", [
      ...(section.questions || []),
      { ...base, ...withOptions },
    ]);
    setAddMenuOpen(false);
  };

  const updateQuestion = (qIdx, updatedQ) => {
    const arr = [...(section.questions || [])];
    arr[qIdx] = { ...updatedQ, isChanged: !updatedQ.isNew };
    updateField("questions", arr);
  };

  const deleteQuestion = (qIdx) => {
    if (restricted) {
      toast.error("This test has responses — questions can't be removed.");
      return;
    }
    const q = section.questions[qIdx];
    if (q.isNew)
      updateField(
        "questions",
        section.questions.filter((_, i) => i !== qIdx),
      );
    else {
      const arr = [...section.questions];
      arr[qIdx] = { ...q, isDeleted: true };
      updateField("questions", arr);
    }
  };

  const duplicateQuestion = (qIdx) => {
    if (restricted) {
      toast.error("This test has responses — questions can't be duplicated.");
      return;
    }
    const q = section.questions[qIdx];
    const copy = {
      ...q,
      questionId: null,
      isNew: true,
      isChanged: false,
      isDeleted: false,
      questionText: q.questionText ? `${q.questionText} (copy)` : "",
      answerOptions: (q.answerOptions || [])
        .filter((o) => !o.isDeleted)
        .map((o) => ({
          ...o,
          id: null,
          isNew: true,
          isChanged: false,
          isDeleted: false,
        })),
    };
    const arr = [...section.questions];
    arr.splice(qIdx + 1, 0, copy);
    updateField("questions", arr);
  };

  // ── drag reorder ──────────────────────────────────────────────────────
  const reorderQuestions = (fromAi, toAi) => {
    const arr = [...section.questions];
    const [moved] = arr.splice(fromAi, 1);
    arr.splice(toAi, 0, moved);
    // reassign questionOrder to match new visible order
    let order = 1;
    const withOrder = arr.map((q) =>
      q.isDeleted ? q : { ...q, questionOrder: order++, isChanged: !q.isNew },
    );
    updateField("questions", withOrder);
  };

  // Interpretations and recommendations are edited as one paired "band":
  // the score range is shared and kept in sync across both lists, while
  // the title/description text stays independent per list.
  const changeBandRange = (i, key, val) => {
    const numVal = val === "" ? null : Number(val);
    let interpArr = applyBandSync(
      [...(section.sectionInterpretation || [])].map((it, x) =>
        x === i ? { ...it, [key]: numVal, isChanged: !it.isNew } : it,
      ),
      i,
      key,
      numVal,
    );
    let recArr = applyBandSync(
      [...(section.sectionRecommendations || [])].map((it, x) =>
        x === i ? { ...it, [key]: numVal, isChanged: !it.isNew } : it,
      ),
      i,
      key,
      numVal,
    );
    onUpdate({
      ...section,
      sectionInterpretation: interpArr,
      sectionRecommendations: recArr,
      isChanged: !section.isNew,
    });
  };

  const changeInterpText = (i, key, val) => {
    const arr = [...(section.sectionInterpretation || [])];
    arr[i] = { ...arr[i], [key]: val, isChanged: !arr[i].isNew };
    updateField("sectionInterpretation", arr);
  };
  const changeRecText = (i, key, val) => {
    const arr = [...(section.sectionRecommendations || [])];
    arr[i] = { ...arr[i], [key]: val, isChanged: !arr[i].isNew };
    updateField("sectionRecommendations", arr);
  };

  const addBand = () => {
    if (restricted) {
      toast.error("This test has responses — new score bands can't be added.");
      return;
    }
    onUpdate({
      ...section,
      sectionInterpretation: [
        ...(section.sectionInterpretation || []),
        blankInterp(),
      ],
      sectionRecommendations: [
        ...(section.sectionRecommendations || []),
        blankRec(),
      ],
      isChanged: !section.isNew,
    });
  };

  const delBand = (i) => {
    if (restricted) {
      toast.error("This test has responses — score bands can't be removed.");
      return;
    }
    const visible = (section.sectionInterpretation || []).filter(
      (x) => !x.isDeleted,
    );
    if (visible.length <= 1) return;

    const interpItem = section.sectionInterpretation[i];
    const recItem = section.sectionRecommendations[i];
    const nextInterp = interpItem?.isNew
      ? section.sectionInterpretation.filter((_, x) => x !== i)
      : section.sectionInterpretation.map((it, x) =>
          x === i ? { ...it, isDeleted: true } : it,
        );
    const nextRec = recItem?.isNew
      ? section.sectionRecommendations.filter((_, x) => x !== i)
      : section.sectionRecommendations.map((it, x) =>
          x === i ? { ...it, isDeleted: true } : it,
        );
    onUpdate({
      ...section,
      sectionInterpretation: nextInterp,
      sectionRecommendations: nextRec,
      isChanged: !section.isNew,
    });
  };

  // Auto-build N interpretation + N recommendation bands that evenly (and
  // contiguously) cover 0..sectionMaxScore. Existing bands are soft-deleted
  // (or dropped, if never saved) the same way the manual delete buttons do
  // it, then replaced with the freshly generated set.
  const autoGenerateBands = () => {
    if (restricted) {
      toast.error("This test has responses — bands can't be regenerated.");
      return;
    }
    const total = getSectionMaxScore(section);
    const bands = generateScoreBands(total, bandCount);

    const retireAll = (list) =>
      (list || []).flatMap((item) =>
        item.isDeleted
          ? [item]
          : item.isNew
            ? []
            : [{ ...item, isDeleted: true }],
      );

    const newInterp = bands.map((b) => ({
      id: null,
      title: "",
      minScore: b.minScore,
      maxScore: b.maxScore,
      description: "",
      isNew: true,
      isChanged: false,
      isDeleted: false,
    }));
    const newRec = bands.map((b) => ({
      id: null,
      title: "",
      minScore: b.minScore,
      maxScore: b.maxScore,
      recommendationText: "",
      isNew: true,
      isChanged: false,
      isDeleted: false,
    }));

    onUpdate({
      ...section,
      sectionInterpretation: [
        ...retireAll(section.sectionInterpretation),
        ...newInterp,
      ],
      sectionRecommendations: [
        ...retireAll(section.sectionRecommendations),
        ...newRec,
      ],
      isChanged: !section.isNew,
    });
  };

  const sectionMaxScore = getSectionMaxScore(section);

  const q = searchQuery.trim().toLowerCase();
  const visibleQ = (section.questions || [])
    .filter((qq) => !qq.isDeleted)
    .filter((qq) => !q || qq.questionText?.toLowerCase().includes(q));
  const visibleInterp = (section.sectionInterpretation || []).filter(
    (i) => !i.isDeleted,
  );
  const visibleRec = (section.sectionRecommendations || []).filter(
    (r) => !r.isDeleted,
  );
  const bands = visibleInterp.map((interpItem, k) => {
    const recItem = visibleRec[k] || blankRec();
    return {
      interpItem,
      recItem,
      interpAi: section.sectionInterpretation.findIndex(
        (x) => x === interpItem,
      ),
      recAi: section.sectionRecommendations.findIndex((x) => x === recItem),
    };
  });
  const totalQ = (section.questions || []).filter(
    (qq) => !qq.isDeleted,
  ).length;
  return (
    <div className="sec" id={`section-${sectionDomId}`}>
      {/* ── header ─────────────────────────────────────────────────────────── */}
      <div className="sec__hd">
        <IconButton
          className="sec__toggle"
          icon={open ? "mdi:chevron-down" : "mdi:chevron-right"}
          variant="edit"
          onClick={() => setOpen(!open)}
          title={open ? "Collapse section" : "Expand section"}
        />

        <span className="sec__index">{index + 1}</span>

        <input
          className="sec__title"
          placeholder={`Section ${index + 1} title…`}
          title="Give this section a descriptive name"
          value={section.sectionTitle || ""}
          onChange={(e) => updateField("sectionTitle", e.target.value)}
        />

        <div className="sec__hd-right">
          <label
            className="sec__shuffle"
            title="Show questions in random order to each respondent"
          >
            <input
              type="checkbox"
              checked={section.randomizeQuestions || false}
              onChange={(e) =>
                updateField("randomizeQuestions", e.target.checked)
              }
            />
            <Icon icon="mdi:shuffle-variant" />
            Shuffle
          </label>

          {section.isNew && (
            <span className="sec__badge sec__badge--new">New</span>
          )}
          {section.isChanged && !section.isNew && (
            <span
              className="sec__badge sec__badge--mod"
              title="Unsaved changes"
            >
              •
            </span>
          )}
          <span className="sec__qcount">
            {totalQ} question{totalQ !== 1 ? "s" : ""}
          </span>
          {!restricted && (
            <IconButton
              className="sec__del"
              icon="material-symbols-light:delete-outline"
              variant="delete"
              onClick={onDelete}
              title="Delete this section and all its questions"
            />
          )}
        </div>
      </div>

      {open && (
        <div className="sec__body">
          {/* ── questions ────────────────────────────────────────────────── */}
          <div className="sec__block-hd">
            <Icon icon="mdi:format-list-checks" />
            Questions <span>{totalQ}</span>
          </div>
          {restricted && (
            <p className="sec__restricted-note">
              <Icon icon="mdi:lock-outline" /> This test has responses, so
              existing questions can be edited but none can be added or removed.
            </p>
          )}
          <div className="sec__questions">
            {visibleQ.length === 0 && !q && !restricted && (
              <div className="sec__empty">
                <Icon icon="mdi:comment-question-outline" />
                <p>No questions yet in this section</p>
                <ButtonWithIcon
                  className="add-btn"
                  icon="mdi:plus-circle-outline"
                  onClick={() => addQuestion("SINGLE_CHOICE")}
                  text="Add your first question"
                  type="button"
                />
              </div>
            )}

            {visibleQ.length === 0 && q && (
              <p className="sec__no-match">
                No questions match “{searchQuery}” in this section
              </p>
            )}

            {visibleQ.length === 0 && !q && restricted && (
              <p className="sec__no-match">No questions in this section.</p>
            )}

            {visibleQ.map((question, vi) => {
              const ai = section.questions.findIndex((x) => x === question);
              return (
                <div
                  key={question.questionId || `q-${ai}`}
                  id={`question-${sectionDomId}-${ai}`}
                  className={
                    dragOverIdx === ai
                      ? "sec__q-dropzone sec__q-dropzone--active"
                      : "sec__q-dropzone"
                  }
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverIdx(ai);
                  }}
                  onDragLeave={() =>
                    setDragOverIdx((cur) => (cur === ai ? null : cur))
                  }
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragOverIdx(null);
                    if (
                      dragFromRef.current !== null &&
                      dragFromRef.current !== ai
                    ) {
                      reorderQuestions(dragFromRef.current, ai);
                    }
                    dragFromRef.current = null;
                  }}
                >
                  <Question
                    question={question}
                    questionIndex={vi + 1}
                    onUpdate={(uq) => updateQuestion(ai, uq)}
                    onDelete={() => deleteQuestion(ai)}
                    onDuplicate={() => duplicateQuestion(ai)}
                    restricted={restricted}
                    defaultExpanded={question.isNew && !question.questionText}
                    focusToken={
                      questionFocus?.ai === ai ? questionFocus.token : null
                    }
                    dragHandleProps={{
                      draggable: true,
                      onDragStart: () => {
                        dragFromRef.current = ai;
                      },
                      onDragEnd: () => {
                        dragFromRef.current = null;
                        setDragOverIdx(null);
                      },
                    }}
                  />
                </div>
              );
            })}

            {visibleQ.length > 0 && !restricted && (
              <div className="sec__add-wrap">
                <ButtonWithIcon
                  className="sec__add"
                  icon="mdi:plus-circle-outline"
                  onClick={() => setAddMenuOpen((v) => !v)}
                  text="Add question"
                  type="button"
                />
                {addMenuOpen && (
                  <div className="sec__add-menu">
                    {QUESTION_TYPE_LIST.map((t) => (
                      <button
                        key={t.value}
                        type="button"
                        className="sec__add-opt"
                        style={{
                          "--opt-color": t.color,
                          "--opt-color-soft": t.colorSoft,
                        }}
                        onClick={() => addQuestion(t.value)}
                      >
                        <Icon icon={t.icon} />
                        {t.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── scoring — lives right under this section's questions, so ─── */}
          {/* it's unambiguous which section it belongs to. ──────────────── */}
          <div className="sec__block-hd sec__block-hd--scoring">
            <Icon icon="mdi:chart-bell-curve-cumulative" />
            Scoring for “
            {section.sectionTitle?.trim() || `Section ${index + 1}`}”
          </div>
          <div className="sec__scoring">
            <div className="sec__scoring-summary">
              <div className="sec__scoring-total">
                <strong>Total possible score for this section: </strong>
                {sectionMaxScore}
              </div>
              <label className="sec__scoring-divider">
                Divide into
                <input
                  type="number"
                  min="1"
                  value={bandCount}
                  onChange={(e) =>
                    setBandCount(Math.max(1, Number(e.target.value) || 1))
                  }
                />
                bands
              </label>
              {!restricted && (
                <ButtonWithIcon
                  className="add small"
                  icon="mdi:auto-fix"
                  onClick={autoGenerateBands}
                  text="Auto-generate bands"
                  type="button"
                />
              )}
            </div>

            {restricted && (
              <p className="sec__restricted-note">
                <Icon icon="mdi:lock-outline" /> This test has responses — you
                can edit these bands but can't add or remove any.
              </p>
            )}

            <div className="sec__band-list">
              {bands.map(({ interpItem, recItem, interpAi, recAi }, k) => (
                <BandCard
                  key={interpItem.id || `band-${interpAi}`}
                  range={interpItem}
                  onRangeChange={(field, val) =>
                    changeBandRange(interpAi, field, val)
                  }
                  interp={interpItem}
                  onInterpChange={(field, val) =>
                    changeInterpText(interpAi, field, val)
                  }
                  rec={recItem}
                  onRecChange={(field, val) => changeRecText(recAi, field, val)}
                  onDelete={() => delBand(interpAi)}
                  showDelete={!restricted && bands.length > 1}
                />
              ))}
            </div>

            {!restricted && (
              <ButtonWithIcon
                className="add small sec__band-add"
                icon="mdi:plus-circle-outline"
                onClick={addBand}
                text="Add score band"
                type="button"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Section;
